import { useEffect, useRef, useState } from "react";
import { useAdminStore } from "../../state/adminStore";
import { ConfirmDialog } from "./ConfirmDialog";
import { DangerDialog } from "./DangerDialog";
import { KebabMenu, type KebabAction } from "./KebabMenu";
import { StatusPill } from "./StatusPill";
import { relativeTime, shortDate } from "../../lib/relativeTime";
import type { AdminUserRow, UserSortKey } from "../../lib/api";

interface SortOption {
  key: UserSortKey;
  label: string;
}

const SORT_OPTIONS: SortOption[] = [
  { key: "created_at", label: "Registriert" },
  { key: "last_activity_at", label: "Letzte Aktivität" },
  { key: "email", label: "Email" },
  { key: "role", label: "Rolle" },
  { key: "status", label: "Status" },
  { key: "answers_total", label: "Antworten" },
  { key: "last_login_at", label: "Letzter Login" },
];

function sortLabel(key: UserSortKey): string {
  return SORT_OPTIONS.find((o) => o.key === key)?.label ?? key;
}

type DialogChoice = "block" | "unblock" | "delete" | null;

interface UserRowActionsProps {
  user: AdminUserRow;
  onBlock: (id: string) => Promise<void>;
  onUnblock: (id: string) => Promise<void>;
  onSoftDelete: (id: string) => Promise<void>;
}

function UserRowActions({ user, onBlock, onUnblock, onSoftDelete }: UserRowActionsProps) {
  // Dialog state lives in the row, not the menu — so the menu can fully
  // close before any dialog mounts. Nesting Radix Popover inside Radix Dialog
  // triggers focus-trap fights that show up as the dialog never opening.
  const [dialog, setDialog] = useState<DialogChoice>(null);

  if (user.self) return <span data-testid={`actions-self-${user.id}`} className="text-fg-faint text-xs">—</span>;

  const actions: KebabAction[] = [
    {
      label: user.status === "blocked" ? "Freigeben" : "Blocken",
      tone: user.status === "blocked" ? "default" : "warning",
      testid: user.status === "blocked" ? `unblock-${user.id}` : `block-${user.id}`,
      onSelect: () => setDialog(user.status === "blocked" ? "unblock" : "block"),
    },
    {
      label: "Löschen",
      tone: "danger",
      testid: `soft-delete-${user.id}`,
      onSelect: () => setDialog("delete"),
    },
  ];

  return (
    <>
      <KebabMenu
        testid={`actions-${user.id}`}
        ariaLabel={`Aktionen für ${user.email}`}
        actions={actions}
      />
      {dialog === "block" && (
        <ConfirmDialog
          defaultOpen
          onOpenChange={(o) => { if (!o) setDialog(null); }}
          title="User wirklich blocken?"
          description={
            <span>
              <span className="font-medium text-fg">{user.email}</span> kann sich nicht mehr einloggen, alle laufenden Sessions werden beendet. Du kannst den User später wieder freigeben.
            </span>
          }
          confirmLabel="Blocken"
          onConfirm={() => onBlock(user.id)}
        />
      )}
      {dialog === "unblock" && (
        <ConfirmDialog
          defaultOpen
          onOpenChange={(o) => { if (!o) setDialog(null); }}
          title="User wieder freigeben?"
          description={
            <span>
              <span className="font-medium text-fg">{user.email}</span> kann sich anschließend wieder einloggen.
            </span>
          }
          confirmLabel="Freigeben"
          onConfirm={() => onUnblock(user.id)}
        />
      )}
      {dialog === "delete" && (
        <DangerDialog
          defaultOpen
          onOpenChange={(o) => { if (!o) setDialog(null); }}
          title="User unwiderruflich löschen?"
          description={
            <span>
              <span className="font-medium text-fg">{user.email}</span> wird soft-deleted: Email anonymisiert, Passwort entfernt, Sessions + Fortschritt + History gelöscht. Die Invite-Bezeichnung im Audit bleibt erhalten. <strong>Nicht rückgängig zu machen.</strong>
            </span>
          }
          confirmPhrase={user.email}
          confirmLabel="Löschen"
          onConfirm={() => onSoftDelete(user.id)}
        />
      )}
    </>
  );
}

interface SortDropdownProps {
  sort: UserSortKey;
  sortDir: "asc" | "desc";
  onChange: (key: UserSortKey, dir: "asc" | "desc") => void;
}

function SortDropdown({ sort, sortDir, onChange }: SortDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (open && ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="users-sort-trigger"
        className="min-h-[44px] inline-flex items-center gap-2 bg-card border border-outline rounded-lg px-3 text-sm text-fg-soft hover:text-fg focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
      >
        <span className="text-xs uppercase tracking-wider text-fg-faint">Sortieren:</span>
        <span className="font-medium text-fg">{sortLabel(sort)}</span>
        <span aria-hidden className="text-fg-muted">{sortDir === "asc" ? "↑" : "↓"}</span>
      </button>
      {open && (
        <div
          role="listbox"
          data-testid="users-sort-menu"
          className="absolute z-30 mt-1 right-0 min-w-[220px] bg-elevated border border-outline rounded-lg shadow-modal py-1 animate-fade-in"
        >
          {SORT_OPTIONS.map((o) => {
            const isActive = o.key === sort;
            return (
              <button
                key={o.key}
                type="button"
                role="option"
                aria-selected={isActive}
                data-testid={`users-sort-${o.key}`}
                onClick={() => {
                  // Same key clicked → toggle direction; new key → default to desc.
                  const nextDir: "asc" | "desc" = isActive
                    ? sortDir === "asc"
                      ? "desc"
                      : "asc"
                    : "desc";
                  onChange(o.key, nextDir);
                  setOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between gap-3 ${
                  isActive ? "bg-bg/40 text-accent font-semibold" : "text-fg-soft hover:bg-bg/40"
                }`}
              >
                <span>{o.label}</span>
                {isActive && (
                  <span aria-hidden className="text-fg-muted">{sortDir === "asc" ? "↑" : "↓"}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AdminUsersTab() {
  const status = useAdminStore((s) => s.usersStatus);
  const error = useAdminStore((s) => s.usersError);
  const users = useAdminStore((s) => s.users);
  const total = useAdminStore((s) => s.total);
  const page = useAdminStore((s) => s.page);
  const pageSize = useAdminStore((s) => s.pageSize);
  const sort = useAdminStore((s) => s.sort);
  const sortDir = useAdminStore((s) => s.sortDir);
  const q = useAdminStore((s) => s.q);
  const inviteCode = useAdminStore((s) => s.inviteCode);
  const usersPending = useAdminStore((s) => s.usersPending);
  const loadUsers = useAdminStore((s) => s.loadUsers);
  const setUsersQuery = useAdminStore((s) => s.setUsersQuery);
  const blockUser = useAdminStore((s) => s.blockUser);
  const unblockUser = useAdminStore((s) => s.unblockUser);
  const softDeleteUser = useAdminStore((s) => s.softDeleteUser);

  const [qInput, setQInput] = useState(q);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const invites = useAdminStore((s) => s.invites);
  const loadInvites = useAdminStore((s) => s.loadInvites);

  useEffect(() => {
    void loadUsers();
    void loadInvites();
  }, [loadUsers, loadInvites]);

  useEffect(() => {
    if (qInput === q) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      void setUsersQuery({ q: qInput });
    }, 200);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section className="space-y-4" data-testid="users-tab">
      {/* Filter + sort bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <span aria-hidden className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-faint">🔍</span>
          <input
            type="search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Suche: Email, Invite-Name oder User-ID …"
            className="w-full min-h-[44px] bg-card border border-outline rounded-xl pl-10 pr-3 text-sm text-fg placeholder:text-fg-faint focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            data-testid="users-search-input"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-fg-faint">
          Invite:
          <select
            value={inviteCode}
            onChange={(e) => void setUsersQuery({ inviteCode: e.target.value })}
            className="min-h-[44px] px-3 pr-9 bg-card border border-outline rounded-lg text-sm text-fg focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 appearance-none"
            data-testid="users-invite-filter"
          >
            <option value="">alle</option>
            {invites
              .filter((inv) => inv.redemption_count > 0)
              .map((inv) => (
                <option key={inv.code} value={inv.code}>
                  {inv.name || "(kein Name)"} — {inv.code}
                </option>
              ))}
          </select>
        </label>
        <SortDropdown
          sort={sort}
          sortDir={sortDir}
          onChange={(key, dir) => void setUsersQuery({ sort: key, sortDir: dir })}
        />
      </div>

      <div className="text-sm font-semibold text-fg-soft">
        {status === "ready" ? `Users · ${total}` : "Users"}
      </div>

      {status === "loading" && <p className="text-fg-muted text-sm animate-pulse">Lade User…</p>}
      {status === "error" && (
        <div className="bg-danger-soft border border-danger/40 rounded-xl px-4 py-3 text-danger text-sm" role="alert">
          Fehler beim Laden: {error}
        </div>
      )}

      {/* Row list — 2-line cards. Same layout on desktop and mobile so the
       *   admin sees the same data hierarchy regardless of viewport. */}
      <div className="bg-card border border-outline rounded-2xl overflow-hidden divide-y divide-outline-soft">
        {users.map((u) => {
          const meta = [
            `Reg. ${shortDate(u.created_at)}`,
            `Aktivität ${relativeTime(u.last_activity_at)}`,
            `${u.answers_total} Antw`,
            `${u.active_days} ${u.active_days === 1 ? "Tag" : "Tage"}`,
            `Code: ${u.invite ? u.invite.name || "(kein Name)" : "—"}`,
          ];
          return (
            <div
              key={u.id}
              data-testid={`user-row-${u.id}`}
              className={`px-4 py-3 ${u.self ? "bg-bg/30" : ""}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                  <span
                    className="text-sm font-medium text-fg truncate max-w-full"
                    title={u.email}
                  >
                    {u.email}
                  </span>
                  {u.self && (
                    <span className="text-xs text-accent font-medium shrink-0" data-testid="self-marker">
                      (du)
                    </span>
                  )}
                </div>
                <span
                  className="text-xs px-2 py-0.5 rounded bg-bg/40 text-fg-muted capitalize shrink-0"
                  data-testid={`role-${u.id}`}
                >
                  {u.role}
                </span>
                <StatusPill tone={u.status === "blocked" ? "danger" : "success"}>
                  {u.status === "blocked" ? "geblockt" : "aktiv"}
                </StatusPill>
                <UserRowActions
                  user={u}
                  onBlock={blockUser}
                  onUnblock={unblockUser}
                  onSoftDelete={softDeleteUser}
                />
              </div>
              <div
                className="mt-1 text-xs text-fg-muted tabular truncate"
                data-testid={`user-meta-${u.id}`}
              >
                {meta.join(" · ")}
              </div>
            </div>
          );
        })}
        {status === "ready" && users.length === 0 && (
          <div className="px-4 py-6 text-center text-fg-muted text-sm">
            Keine User passen zur aktuellen Suche/Filter.
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center gap-2 text-sm" data-testid="pagination">
          <button
            type="button"
            onClick={() => void setUsersQuery({ page: 1 })}
            disabled={page === 1 || usersPending}
            aria-label="erste Seite"
            className="min-h-[36px] px-2 rounded-md border border-outline text-fg-soft hover:bg-card disabled:opacity-40"
          >
            «
          </button>
          <button
            type="button"
            onClick={() => void setUsersQuery({ page: page - 1 })}
            disabled={page === 1 || usersPending}
            aria-label="vorherige Seite"
            className="min-h-[36px] px-2 rounded-md border border-outline text-fg-soft hover:bg-card disabled:opacity-40"
          >
            ‹
          </button>
          <span className="text-fg-soft tabular">
            Seite {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => void setUsersQuery({ page: page + 1 })}
            disabled={page >= totalPages || usersPending}
            aria-label="nächste Seite"
            className="min-h-[36px] px-2 rounded-md border border-outline text-fg-soft hover:bg-card disabled:opacity-40"
          >
            ›
          </button>
          <button
            type="button"
            onClick={() => void setUsersQuery({ page: totalPages })}
            disabled={page >= totalPages || usersPending}
            aria-label="letzte Seite"
            className="min-h-[36px] px-2 rounded-md border border-outline text-fg-soft hover:bg-card disabled:opacity-40"
          >
            »
          </button>
          <span className="ml-auto text-fg-faint text-xs">{total} Einträge gesamt</span>
        </div>
      )}
    </section>
  );
}
